import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Platform, View } from 'react-native';
import { z } from 'zod';

import { ControlledOptionRows, FieldBlock, SwitchRow } from '@/components/services/wizard/field';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PageLayout } from '@/components/ui/page-layout';
import { SkeletonCard } from '@/components/ui/skeleton';
import {
  getAccountStatusLabel,
  SETTINGS_COLLECTION,
  TEAM_PAYMENT_STATUS,
} from '@/content/payments';
import { useCanManagePaymentAccount, useMembership } from '@/hooks/use-membership';
import { usePaymentAccount } from '@/hooks/use-payment-account';
import {
  useBackToSolo,
  useOrganization,
  useOrganizationModes,
  useTeamMembers,
} from '@/hooks/use-team';
import { useTerms } from '@/hooks/use-terms';
import { showToast } from '@/store/toastStore';
import { ClientVisibility, MessagingMode, Organization, PaymentMode } from '@/types/organization';
import { PAYMENT_ACCOUNT_STATUS, PAYMENT_COLLECTION_MODE } from '@/types/payment-account';
import { getApiErrorCode, getApiErrorMessage } from '@/utils/api-error';

const schema = z.object({
  name: z.string().trim().min(2, 'Escreva o nome.').max(80, 'Nome longo demais.'),
  paymentMode: z.enum(['ORGANIZATION', 'PROFESSIONAL'], {
    errorMap: () => ({ message: 'Escolha quem recebe.' }),
  }),
  messagingMode: z.enum(['ORGANIZATION', 'PROFESSIONAL'], {
    errorMap: () => ({ message: 'Escolha o WhatsApp.' }),
  }),
  clientVisibility: z.enum(['SHARED', 'PRIVATE'], {
    errorMap: () => ({ message: 'Escolha quem vê os clientes.' }),
  }),
});

type Form = z.infer<typeof schema>;

/** `code` do 409 de `PATCH /organization/me` ao trocar `paymentMode` com o automático ativo (T7.1/4.7). */
const PAYMENT_MODE_CONFLICT_CODES = new Set([
  'PAYMENT_MODE_AUTOMATIC_ACTIVE',
  'PAYMENT_MODE_ACCOUNT_ACTIVE',
]);

function toForm(org: Organization): Partial<Form> {
  return {
    name: org.name,
    paymentMode: org.paymentMode ?? undefined,
    messagingMode: org.messagingMode ?? undefined,
    clientVisibility: org.clientVisibility ?? undefined,
  };
}

function StepCard({ children }: { children: React.ReactNode }) {
  return (
    <View
      className="gap-4 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      {children}
    </View>
  );
}

/**
 * Como a organização trabalha: os três modos da seção 4, um a um, para quem é
 * meio a meio entre "caixa único" e "cada um por si". No fim, voltar a ser
 * solo — só sem outro membro ativo.
 */
export default function TeamSettingsScreen() {
  const terms = useTerms();
  const { membership, can } = useMembership();
  const allowed = can('team');
  const { data: organization, isLoading } = useOrganization({ enabled: allowed });
  const { data: members } = useTeamMembers({ enabled: allowed });
  // O dono sempre vê a própria conta — em caixa único é a única subconta que
  // existe (`targetProviderId` resolve pro dono), então serve de "estado da
  // equipe" (T7.2, plano 4.7) sem chamada extra.
  const { data: paymentStatus } = usePaymentAccount({ enabled: allowed });
  // `can('team')` é quem chega nesta tela, não quem gerencia a subconta —
  // em caixa único só o dono ativa/muda (revisão da T7.2: a gerência não
  // pode ver o botão "Ativar"/"Configurar", a API recusaria).
  const canManagePayment = useCanManagePaymentAccount();
  const saveModes = useOrganizationModes();
  const saveAlerts = useOrganizationModes();
  const backToSolo = useBackToSolo();
  const [confirmSolo, setConfirmSolo] = useState(false);
  const [alerts, setAlerts] = useState(false);

  const { control, handleSubmit, reset, formState, setError } = useForm<Form>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
  });

  useEffect(() => {
    if (!organization) return;
    // O switch de avisos salva sozinho e refaz a organização: o que está sendo
    // editado no formulário não pode sumir por isso.
    reset(toForm(organization), { keepDirtyValues: true });
    setAlerts(organization.teamBookingAlerts);
  }, [organization, reset]);

  if (membership && !allowed) {
    return <Redirect href={'/(tabs)/(provider)/profile' as never} />;
  }

  const g = (word: string) => terms.agree(word, 'organization');
  const othersActive = (members ?? []).filter(
    (m) => m.role !== 'OWNER' && m.status !== 'DISABLED',
  ).length;

  const back = () =>
    router.canGoBack() ? router.back() : router.replace('/(tabs)/(provider)/team' as never);

  const onSave = async (form: Form) => {
    try {
      await saveModes.mutateAsync({ ...form, name: form.name.trim() });
      showToast('Configurações salvas.', 'success');
    } catch (err) {
      const code = getApiErrorCode(err);
      // 409 de trocar "Quem recebe" com o automático ativo (T7.1, 4.7): a
      // frase da API vai perto do controle, não num toast/modal genérico.
      if (code && PAYMENT_MODE_CONFLICT_CODES.has(code)) {
        setError('paymentMode', {
          type: 'server',
          message: getApiErrorMessage(err, 'Não foi possível trocar agora.'),
        });
        return;
      }
      showToast(getApiErrorMessage(err, 'Não foi possível salvar agora.'), 'error');
    }
  };

  const toggleAlerts = async (next: boolean) => {
    setAlerts(next);
    try {
      await saveAlerts.mutateAsync({ teamBookingAlerts: next });
    } catch (err) {
      setAlerts(!next);
      showToast(getApiErrorMessage(err, 'Não foi possível salvar agora.'), 'error');
    }
  };

  const onBackToSolo = async () => {
    setConfirmSolo(false);
    try {
      await backToSolo.mutateAsync();
      showToast('Pronto, a conta voltou a ser solo.', 'success');
      router.replace('/(tabs)/(provider)/profile' as never);
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Não foi possível voltar a ser solo agora.'), 'error');
    }
  };

  return (
    <>
      <PageLayout
        title="Configurações da equipe"
        onBack={back}
        footer={
          formState.isDirty ? (
            <View className="border-t border-neutral-100 bg-card px-5 py-3">
              <Button
                title={saveModes.isPending ? 'Salvando…' : 'Salvar'}
                onPress={handleSubmit(onSave)}
                disabled={saveModes.isPending}
              />
            </View>
          ) : undefined
        }
      >
        {isLoading || !organization ? (
          <View className="gap-3">
            <SkeletonCard showHeader lines={2} />
            <SkeletonCard showHeader lines={3} />
          </View>
        ) : (
          <>
            <StepCard>
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <FieldBlock
                    title={`Nome ${g('do')} ${terms.organization}`}
                    error={fieldState.error?.message}
                  >
                    <Input
                      value={field.value ?? ''}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                      autoCapitalize="words"
                      style={Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined}
                    />
                  </FieldBlock>
                )}
              />
            </StepCard>

            <StepCard>
              <ControlledOptionRows<Form, PaymentMode>
                control={control}
                name="paymentMode"
                title="Quem recebe"
                options={[
                  {
                    value: 'ORGANIZATION',
                    label: terms.title.organization,
                    meaning: `Pix e link ${g('do')} ${terms.organization} em toda cobrança. Só você confirma os pagamentos.`,
                  },
                  {
                    value: 'PROFESSIONAL',
                    label: `Cada ${terms.professional}`,
                    meaning: 'Cada um usa os próprios dados de pagamento.',
                  },
                ]}
              />
              <ControlledOptionRows<Form, MessagingMode>
                control={control}
                name="messagingMode"
                title="WhatsApp que fala com o cliente"
                options={[
                  {
                    value: 'ORGANIZATION',
                    label: `Número ${g('do')} ${terms.organization}`,
                    meaning: 'Tudo sai de um número só.',
                  },
                  {
                    value: 'PROFESSIONAL',
                    label: `Número de cada ${terms.professional}`,
                    meaning: `Sem conexão, sai pelo número ${g('do')} ${terms.organization}.`,
                  },
                ]}
              />
              <ControlledOptionRows<Form, ClientVisibility>
                control={control}
                name="clientVisibility"
                title={terms.title.clientPlural}
                options={[
                  {
                    value: 'SHARED',
                    label: 'Compartilhados',
                    meaning: 'Todos veem e marcam para qualquer um.',
                  },
                  {
                    value: 'PRIVATE',
                    label: 'Cada um os seus',
                    meaning: 'Você continua vendo todos.',
                  },
                ]}
              />
            </StepCard>

            {organization.paymentMode === 'ORGANIZATION' && (
              <StepCard>
                <FieldBlock
                  title={TEAM_PAYMENT_STATUS.title}
                  note={
                    paymentStatus?.account
                      ? getAccountStatusLabel(paymentStatus.account.status) +
                        (paymentStatus.account.status === PAYMENT_ACCOUNT_STATUS.ACTIVE
                          ? ` · ${
                              paymentStatus.account.paymentCollection ===
                              PAYMENT_COLLECTION_MODE.AUTOMATIC
                                ? SETTINGS_COLLECTION.onStatus
                                : SETTINGS_COLLECTION.offStatus
                            }`
                          : '')
                      : TEAM_PAYMENT_STATUS.notStarted
                  }
                >
                  <ThemedText className="text-sm leading-5 text-neutral-500">
                    {canManagePayment ? TEAM_PAYMENT_STATUS.body : TEAM_PAYMENT_STATUS.readOnlyNote}
                  </ThemedText>
                </FieldBlock>
                {canManagePayment && (
                  <Button
                    variant="outline"
                    title={
                      paymentStatus?.account
                        ? TEAM_PAYMENT_STATUS.manageButton
                        : TEAM_PAYMENT_STATUS.activateButton
                    }
                    onPress={() =>
                      router.push(
                        (paymentStatus?.account
                          ? '/(tabs)/(provider)/payments/settings'
                          : '/(tabs)/(provider)/payments/about') as never,
                      )
                    }
                    testID="team-settings-payment-status"
                  />
                )}
              </StepCard>
            )}

            <StepCard>
              <SwitchRow
                title={`Avisos de ${terms.appointment} da equipe`}
                note={
                  alerts
                    ? `Os de cada ${terms.professional} chegam para você também`
                    : 'Só os da sua agenda'
                }
                checked={alerts}
                onChange={(next) => void toggleAlerts(next)}
              />
            </StepCard>

            <StepCard>
              <FieldBlock
                title="Voltar a ser solo"
                note={othersActive > 0 ? `Desative ${othersActive} antes` : undefined}
              >
                <ThemedText className="text-sm leading-5 text-neutral-500">
                  A equipe some do menu. Quem foi desativado continua guardado.
                </ThemedText>
              </FieldBlock>
              <Button
                variant="outline"
                title="Voltar a ser solo"
                onPress={() => setConfirmSolo(true)}
                disabled={othersActive > 0 || backToSolo.isPending}
              />
            </StepCard>
          </>
        )}
      </PageLayout>

      <Dialog open={confirmSolo} onOpenChange={setConfirmSolo}>
        <DialogContent className="w-[90%] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg text-foreground">Voltar a ser solo?</DialogTitle>
            <DialogDescription>
              Os modos de operação são apagados. Dá para virar equipe de novo pelo Perfil.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex-row flex-wrap gap-3">
            <Button
              title="Cancelar"
              variant="outline"
              className="grow basis-32"
              onPress={() => setConfirmSolo(false)}
            />
            <Button
              title="Voltar a ser solo"
              className="grow basis-32"
              onPress={() => void onBackToSolo()}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
